import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '@/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Mail, Lock, CheckCircle } from 'lucide-react';

export default function CustomerResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Request reset
  const [email, setEmail] = useState('');

  // Perform reset
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');

  async function handleRequestReset(e) {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError('Bitte gib deine E-Mail-Adresse ein.');
      return;
    }
    setLoading(true);

    try {
      await api.customerRequestPasswordReset(email);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Anfrage fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    setError(null);

    if (!newPassword || !newPasswordConfirm) {
      setError('Bitte füll alle Felder aus.');
      return;
    }

    if (newPassword !== newPasswordConfirm) {
      setError('Passwörter stimmen nicht überein');
      return;
    }

    if (newPassword.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    setLoading(true);

    try {
      await api.customerResetPassword(token, newPassword);
      setSuccess(true);
      setTimeout(() => {
        navigate('/customer/login');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Zurücksetzen fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">ScanMirEin</h1>
          <p className="text-muted-foreground">Passwort zurücksetzen</p>
        </div>

        <Card>
          {!token ? (
            <>
              <CardHeader>
                <CardTitle>Passwort zurücksetzen</CardTitle>
                <CardDescription>
                  Wir senden dir einen Link zum Zurücksetzen per E-Mail
                </CardDescription>
              </CardHeader>
              <CardContent>
                {success ? (
                  <Alert className="border-green-500 bg-green-50">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-900">
                      <strong>E-Mail gesendet!</strong> Prüfe dein Postfach und folge dem Link
                      zum Zurücksetzen deines Passworts.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <form onSubmit={handleRequestReset} className="space-y-4" noValidate>
                    {error && (
                      <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="email">E-Mail-Adresse</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="deine@email.de"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10"
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Reset-Link anfordern
                    </Button>
                  </form>
                )}
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader>
                <CardTitle>Neues Passwort setzen</CardTitle>
                <CardDescription>
                  Wähle ein sicheres neues Passwort
                </CardDescription>
              </CardHeader>
              <CardContent>
                {success ? (
                  <Alert className="border-green-500 bg-green-50">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-900">
                      <strong>Passwort geändert!</strong> Du wirst in Kürze zum Login weitergeleitet.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <form onSubmit={handleResetPassword} className="space-y-4" noValidate>
                    {error && (
                      <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="new-password">Neues Passwort (min. 8 Zeichen)</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="new-password"
                          type="password"
                          placeholder="••••••••"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="pl-10"
                          required
                          minLength={8}
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="new-password-confirm">Passwort wiederholen</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="new-password-confirm"
                          type="password"
                          placeholder="••••••••"
                          value={newPasswordConfirm}
                          onChange={(e) => setNewPasswordConfirm(e.target.value)}
                          className="pl-10"
                          required
                          minLength={8}
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Passwort ändern
                    </Button>
                  </form>
                )}
              </CardContent>
            </>
          )}
        </Card>

        <div className="text-center mt-6">
          <Link to="/customer/login" className="text-sm text-muted-foreground hover:underline">
            ← Zurück zum Login
          </Link>
        </div>
      </div>
    </div>
  );
}
